import { css, jsx } from '@emotion/core';
import { Card, Icon } from 'antd';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { bp, colors } from '@/styles';

import ContactCardImage from './ContactCardImage';
import ContactInitials from './ContactInitials';

const style = {
  card: css`
    align-items: flex-start;
    display: flex;
    flex: 0 0 40%;
    margin-bottom: 32px;

    &:nth-child(odd) {
      margin-right: 1.6rem;
    }

    &:last-child {
      margin-bottom: 0;
    }

    @media print {
      flex: 0 0 50%;
      margin-bottom: 12px;
      margin-right: 0 !important;
    }
  `,
  cardSmall: css`
    background-color: #ffffff;
    width: 100%;
    margin-right: auto;
    margin-left: auto;

    @media ${bp.sm} {
      margin-left: 0;
    }

    .ant-card-head {
      border: none;
      padding: 0;

      &-title {
        color: ${colors.secondaryGrayDark};
        font-weight: 900;
        font-size: 2.8rem;
        line-height: 1.29;
        padding: 0;
      }
    }
    .ant-card-body {
      padding: 0;

      @media print {
        padding-left: 12px;
        padding-right: 12px;
      }
    }
    @media print {
      .ant-card-head {
        padding: 0px;
        border-bottom: none;
        &-title {
          padding: 24px 0px 12px;
          font-size: 2.2rem;
        }
      }
    }
  `,
  container: css`
    width: 100%;

    @media print {
      margin-bottom: 26px;
    }
  `,
  title: css`
    font-size: 2.8rem;
    font-weight: 900;
    margin: 0 0 2.6rem;

    @media ${bp.md} {
      font-size: 3.6rem;
    }
  `,
  inner: css`
    @media ${bp.md} {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }

    @media print {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }
  `,
  contactInfo: {
    inner: css``,
    container: css`
      margin-left: 16px;
      margin-bottom: 8px;
    `,
    name: css`
      color: ${colors.primaryBlue};
      font-size: 1.6rem;
      font-weight: 900;
      letter-spacing: 0;
      line-height: 1.75;
      margin: 0.4rem 0;
      @media print {
        margin: 0;
      }
    `,
    title: css`
      font-size: 1.2rem;
      font-weight: 900;
      line-height: 1.66;
      margin: 0 0 0.4rem;
      text-transform: uppercase;
      @media print {
        margin: 0;
      }
    `,
    company: css`
      color: ${colors.secondaryGrayDark};
      font-size: 12px;
      line-height: 20px;
      font-weight: 900;
      height: 23px;
      padding: 1px 14px;
      border-radius: 100px;
      background-color: ${colors.accentGrayUltralight};
    `,
    link: css`
      color: ${colors.primaryBlue};
      display: block;
      font-size: 1.2rem;
      font-weight: 900;
      line-height: 1.66;
      margin: 0 0 0.4rem;

      &:hover {
        color: ${colors.primaryRed};
      }
      .anticon {
        font-size: 14px;
        margin-right: 9px;
      }

      @media print {
        margin: 4px 0 0;
      }
    `,
  },
  cardVertical: css`
    box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);

    .ant-card-head {
      &-title {
        padding: 2.4rem 0;

        > * {
          margin: 0;
        }
      }
    }
  `,
};

class ContactCard extends PureComponent {
  render() {
    const { contacts, title, setContactPhoto, layout = 'horizontal', isNewsRelease } = this.props;

    if (!contacts.length) {
      return null;
    }
    return (
      <div css={style.container} id="contact-cards">
        <Card
          title={<h3 css={style.title}>{title}</h3>}
          bordered={false}
          css={layout === 'horizontal' ? style.cardSmall : style.cardVertical}
        >
          <div css={layout === 'horizontal' ? style.inner : {}}>
            {contacts.map(contact => {
              const {
                fullname,
                phone,
                email,
                title: contactTitle,
                id,
                displayPicture,
                initials,
                companyid,
                company,
              } = contact;

              return (
                <div key={fullname} css={style.card}>
                  {id && displayPicture ? (
                    <ContactCardImage
                      setContactPhoto={setContactPhoto}
                      id={id}
                      name={fullname}
                      initials={initials}
                    />
                  ) : (
                    <ContactInitials icon="user" initials={initials} />
                  )}
                  <div css={style.contactInfo.container}>
                    <div css={style.contactInfo.inner}>
                      {companyid !== 4274 && isNewsRelease ? (
                        <p>
                          <span css={style.contactInfo.name}>{fullname}</span> (External)
                        </p>
                      ) : (
                        <p css={style.contactInfo.name}>{fullname}</p>
                      )}

                      {contactTitle ? <p css={style.contactInfo.title}>{contactTitle}</p> : null}

                      {company && companyid !== 4274 ? (
                        <p css={style.contactInfo.company}>{company}</p>
                      ) : null}

                      {phone ? (
                        <a href={`tel:${phone}`} data-attr={fullname} css={style.contactInfo.link}>
                          <Icon type="phone" theme="filled" /> {phone}
                        </a>
                      ) : null}
                      {email ? (
                        <a
                          href={`mailto:${email}`}
                          data-attr={fullname}
                          css={style.contactInfo.link}
                        >
                          <Icon type="mail" /> {email}
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  }
}

ContactCard.propTypes = {
  title: PropTypes.string.isRequired,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      fullname: PropTypes.string.isRequired,
      phone: PropTypes.string,
      email: PropTypes.string,
      imgURL: PropTypes.string,
      isFeatured: PropTypes.bool,
      title: PropTypes.string,
    })
  ).isRequired,
};

export default ContactCard;
